// src/components/video/SettingsModal.tsx
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVideo, faMicrophone, faTimes } from '@fortawesome/free-solid-svg-icons';

interface Device {
  id: string;
  label: string;
}

interface SettingsModalProps {
  videoDevices: Device[];
  audioDevices: Device[];
  selectedVideoDevice?: string;
  selectedAudioDevice?: string;
  onChangeDevice: (kind: 'video' | 'audio', deviceId: string) => void;
  onClose: () => void;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({
  videoDevices,
  audioDevices,
  selectedVideoDevice,
  selectedAudioDevice,
  onChangeDevice,
  onClose,
}) => {
  const handleVideoDeviceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChangeDevice('video', e.target.value);
  };

  const handleAudioDeviceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChangeDevice('audio', e.target.value);
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleModalClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  return (
    <div
      className="fixed inset-0 z-50 overflow-auto flex items-center justify-center"
      onClick={handleBackdropClick}
      style={{ backgroundColor: 'rgba(60, 60, 60, 0.8)' }}
    >
      <div
        className="relative flex flex-col bg-white shadow-xl w-full mx-4"
        onClick={handleModalClick}
        style={{
          borderRadius: '16px',
          maxWidth: '500px',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-4 pb-4">
          <h2 style={{
            color: '#0A0A0A',
            fontFamily: 'Poppins',
            fontSize: '1.25rem',
            fontStyle: 'normal',
            fontWeight: '700',
            lineHeight: '1.75rem',
          }}>
            Settings</h2>
          <button
            onClick={onClose}
            className="cursor-pointer border-0 bg-transparent p-0 flex items-center justify-center"
            aria-label="Close"
            style={{
              width: '24px',
              height: '24px',
              color: '#000',
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18" stroke="#99A1AF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              <path d="M6 6L18 18" stroke="#99A1AF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>

          </button>
        </div>

        {/* Horizontal line */}
        <div className="border-t-2 border-gray-300 mx-0"></div>

        {/* Content */}
        <div className="px-6 py-5">
          {/* Camera Section */}
          <div className="mb-6">
            <div className="flex items-center mb-3 gap-2">
              <div className='w-[2rem] h-[2rem] bg-[#7E57C2] flex items-center justify-center rounded-[0.25rem]'>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.0837 3.33325H7.91699L5.83366 5.83325H3.33366C2.89163 5.83325 2.46771 6.00885 2.15515 6.32141C1.84259 6.63397 1.66699 7.05789 1.66699 7.49992V14.9999C1.66699 15.4419 1.84259 15.8659 2.15515 16.1784C2.46771 16.491 2.89163 16.6666 3.33366 16.6666H16.667C17.109 16.6666 17.5329 16.491 17.8455 16.1784C18.1581 15.8659 18.3337 15.4419 18.3337 14.9999V7.49992C18.3337 7.05789 18.1581 6.63397 17.8455 6.32141C17.5329 6.00885 17.109 5.83325 16.667 5.83325H14.167L12.0837 3.33325Z" stroke="white" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M10 13.3333C11.3807 13.3333 12.5 12.214 12.5 10.8333C12.5 9.45254 11.3807 8.33325 10 8.33325C8.61929 8.33325 7.5 9.45254 7.5 10.8333C7.5 12.214 8.61929 13.3333 10 13.3333Z" stroke="white" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                </svg>

              </div>
              <label className="text-[1rem] text-black m-0">Camera</label>
            </div>
            <div className="relative">
              <select
                value={selectedVideoDevice || ''}
                onChange={handleVideoDeviceChange}
                className="w-full appearance-none cursor-pointer"
                style={{
                  padding: '10px 40px 10px 12px',
                  backgroundColor: '#F3F3F5',
                  border: '1px solid rgba(0, 0, 0, 0.00)',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  color: selectedVideoDevice ? '#000' : '#717182',
                  fontFamily: 'inherit',
                }}
              >
                {videoDevices.length > 0 ? (
                  videoDevices.map((device) => (
                    <option key={device.id} value={device.id}>
                      {device.label}
                    </option>
                  ))
                ) : (
                  <option value="">No cameras found</option>
                )}
              </select>
              {/* <div
                className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
                style={{
                  width: '0',
                  height: '0',
                  borderLeft: '5px solid transparent',
                  borderRight: '5px solid transparent',
                  borderTop: '6px solid #666',
                }}
              ></div> */}
            </div>
          </div>


          {/* Microphone Section */}
          <div>
            <div className="flex items-center mb-3 gap-2">
              <div className='w-[2rem] h-[2rem] bg-[#7E57C2] flex items-center justify-center rounded-[0.25rem]'>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 1.66675C9.33696 1.66675 8.70107 1.93014 8.23223 2.39898C7.76339 2.86782 7.5 3.50371 7.5 4.16675V10.0001C7.5 10.6631 7.76339 11.299 8.23223 11.7678C8.70107 12.2367 9.33696 12.5001 10 12.5001C10.663 12.5001 11.2989 12.2367 11.7678 11.7678C12.2366 11.299 12.5 10.6631 12.5 10.0001V4.16675C12.5 3.50371 12.2366 2.86782 11.7678 2.39898C11.2989 1.93014 10.663 1.66675 10 1.66675Z" stroke="white" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M15.8337 8.33325V9.99992C15.8337 11.547 15.2191 13.0307 14.1251 14.1247C13.0312 15.2187 11.5474 15.8333 10.0003 15.8333C8.45323 15.8333 6.9695 15.2187 5.87554 14.1247C4.78157 13.0307 4.16699 11.547 4.16699 9.99992V8.33325" stroke="white" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M10 15.8333V18.3333" stroke="white" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                </svg>

              </div>
              <label className="text-[1rem] text-black m-0">Microphone</label>
            </div>
            <div className="relative">
              <select
                value={selectedAudioDevice || ''}
                onChange={handleAudioDeviceChange}
                className="w-full appearance-none cursor-pointer"
                style={{
                  padding: '10px 40px 10px 12px',
                  backgroundColor: '#F3F3F5',
                  border: '1px solid rgba(0, 0, 0, 0.00)',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  color: selectedAudioDevice ? '#000' : '#717182',
                  fontFamily: 'inherit',
                }}
              >
                {audioDevices.length > 0 ? (
                  audioDevices.map((device) => (
                    <option key={device.id} value={device.id}>
                      {device.label}
                    </option>
                  ))
                ) : (
                  <option value="">No microphones found</option>
                )}
              </select>
              {/* <div
                className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
                style={{
                  width: '0',
                  height: '0',
                  borderLeft: '5px solid transparent',
                  borderRight: '5px solid transparent',
                  borderTop: '6px solid #666',
                }}
              ></div> */}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end px-6 pb-4">
          <button
            onClick={onClose}
            className="cursor-pointer border-0 transition-colors"
            style={{
              borderRadius: '0.625rem',
              background: '#7E57C2',
              color: '#FFF',
              textAlign: 'center',
              fontFamily: 'Poppins',
              fontSize: '0.875rem',
              fontStyle: 'normal',
              fontWeight: '400',
              lineHeight: '1.25rem',
              display: 'flex',
              width: '6.44238rem',
              height: '3rem',
              padding: '0 0.5rem',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '0.3125rem',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#6D4CAB';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#7E57C2';
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};